import {
  getBrowserFingerprint,
  getStableDeviceId,
  openGraphicCaptcha,
} from "./graphicCaptchaService";
import { reactive, watch, getCurrentScope, onScopeDispose } from "vue";
import { getToken } from "./user";
import { ElMessage } from "element-plus";

const STORAGE_KEY = "sms_send_guard_records_v1";
const WINDOW_MS = 10 * 60 * 1000;
const LIMIT_COUNT = 3;
const COUNTDOWN_SECONDS = 60;
// 等待状态仅保留在本次页面内存中，刷新后由新的短信请求重新确认后端限制。
const waitStates = new Map();

// 内存中的场景标识不使用明文令牌或完整手机号。
function scopeHash(value) {
  let hash = 2166136261;
  for (const char of String(value || "")) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(36);
}

export const SMS_CODE_ENDPOINTS = {
  LOGIN: "/api/user-api/user/mobileCode/login",
  PROFILE_IDENTITY: "/api/user-api/user/mobileCode/profile-identity",
  PROFILE_UPDATE: "/api/user-api/user/mobileCode/profile",
  ALIPAY_WITHDRAW: "/api/user-api/user/mobileCode/alipay",
  BANK_BIND: "/api/user-api/user/mobileCode/bank",
  PASSWORD_RESET: "/api/user-api/user/mobileCode/password-reset",
};

function normalizeActionKey(actionKey) {
  return actionKey || "default";
}

export function createSmsSecurityContext(actionKey) {
  return {
    scene: normalizeActionKey(actionKey),
    deviceId: getStableDeviceId(),
    fingerprint: getBrowserFingerprint(),
  };
}

function loadRecords() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw);
  } catch (error) {
    return {};
  }
}

function saveRecords(records) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(records)); }
  catch { /* 浏览器禁用存储时不能把已发送成功误报成失败。 */ }
}

function getRecentRecords(actionKey) {
  const now = Date.now();
  const records = loadRecords();
  const key = normalizeActionKey(actionKey);
  const list = Array.isArray(records[key]) ? records[key] : [];
  const recentList = list.filter((timestamp) => now - timestamp < WINDOW_MS);
  records[key] = recentList;
  saveRecords(records);
  return recentList;
}

function recordSend(actionKey) {
  const records = loadRecords();
  const key = normalizeActionKey(actionKey);
  const recentList = getRecentRecords(key);
  recentList.push(Date.now());
  records[key] = recentList;
  saveRecords(records);
}

function getRetryAfterSeconds(data = {}) {
  const retryAfterSeconds =
    data.retryAfterSeconds ?? data.data?.retryAfterSeconds;
  if (Number.isFinite(Number(retryAfterSeconds)) && Number(retryAfterSeconds) > 0) {
    return Math.ceil(Number(retryAfterSeconds));
  }

  const retryAfterMinutes =
    data.retryAfterMinutes ?? data.data?.retryAfterMinutes;
  if (Number.isFinite(Number(retryAfterMinutes)) && Number(retryAfterMinutes) > 0) {
    return Math.ceil(Number(retryAfterMinutes) * 60);
  }

  return 0;
}

export function shouldRequireGraphicCaptcha(actionKey) {
  return getRecentRecords(actionKey).length >= LIMIT_COUNT;
}

export async function guardSmsSend(actionKey, options = {}) {
  const securityContext = createSmsSecurityContext(actionKey);
  const requireGraphicCaptcha = shouldRequireGraphicCaptcha(actionKey);

  if (!requireGraphicCaptcha) {
    return {
      verified: false,
      needGraphicCaptcha: false,
      securityContext,
    };
  }

  const verification = await openGraphicCaptcha({
    scene: securityContext.scene,
    deviceId: securityContext.deviceId,
    fingerprint: securityContext.fingerprint,
    title: options.title || "安全验证",
    description: options.description || "拖动下方滑块完成拼图",
    imageUrl: options.imageUrl,
  });

  return {
    verified: true,
    needGraphicCaptcha: true,
    verification,
    securityContext,
  };
}

export async function sendSmsWithGuard(options) {
  const lease = options.controller?.acquire();
  if (options.controller && !lease) {
    throw new Error("短信正在发送或等待中，请稍后重试");
  }
  try {
    return await requestSmsWithGuard(options, lease);
  } finally {
    options.controller?.release(lease);
  }
}

async function requestSmsWithGuard(options, lease) {
  const {
    actionKey,
    endpoint,
    mobile,
    authRequired,
    sendRequest,
    onCountdownStart,
    controller,
    isSuccessResponse,
    title,
    description,
    imageUrl,
  } = options;

  const securityContext = createSmsSecurityContext(actionKey);
  const createPayload = (verification) => ({
    ...(mobile ? { mobile } : {}),
    scene: securityContext.scene,
    deviceId: securityContext.deviceId,
    fingerprint: securityContext.fingerprint,
    verifyToken: verification?.verifyToken || null,
  });
  const requestSms =
    typeof sendRequest === "function"
      ? (verification) =>
          sendRequest({
            verification,
            securityContext,
            payload: createPayload(verification),
          })
      : (verification) => {
          if (!endpoint) {
            throw new Error("短信接口地址不能为空");
          }
          throw new Error("sendRequest is required when no default request client is provided");
        };

  const requestSmsWithRateLimit = async (verification) => {
    try {
      return await requestSms(verification);
    } catch (error) {
      const response = error?.response;
      if (response?.status === 429 || Number(response?.data?.code) === 429) {
        return response;
      }
      throw error;
    }
  };

  let response = await requestSmsWithRateLimit(null);

  if (response?.data?.code === 428 || response?.data?.data?.needCaptcha === true) {
    const verification = await openGraphicCaptcha({
      scene: securityContext.scene,
      deviceId: securityContext.deviceId,
      fingerprint: securityContext.fingerprint,
      authRequired: authRequired === true,
      title: title || "安全验证",
      description: description || "拖动下方滑块完成拼图",
      imageUrl,
    });

    response = await requestSmsWithRateLimit(verification);
  }

  const retryAfterSeconds = getRetryAfterSeconds(response?.data);
  const isRateLimited =
    response?.status === 429 || Number(response?.data?.code) === 429 ||
    response?.data?.data?.limited === true ||
    response?.data?.data?.rateLimited === true ||
    retryAfterSeconds > 0;
  const isSuccess =
    typeof isSuccessResponse === "function"
      ? isSuccessResponse(response)
      : Number(response?.data?.code) === 200;

  if (isSuccess) {
    recordSend(actionKey);
    controller?.start(retryAfterSeconds || COUNTDOWN_SECONDS, "cooldown", lease);
    if (typeof onCountdownStart === "function") {
      onCountdownStart(COUNTDOWN_SECONDS);
    }
  } else if (isRateLimited) {
    // 先反馈本次后端结果，再更新按钮；页面不重复弹出同一条限流提示。
    ElMessage.warning(response?.data?.msg || "验证码发送频率过高，请稍后重试");
    controller?.start(retryAfterSeconds || COUNTDOWN_SECONDS, "limited", lease);
    onCountdownStart?.(retryAfterSeconds || COUNTDOWN_SECONDS);
    return { ...response, smsFeedbackShown: true };
  }

  return response;
}

export function createCountdownController(targetRef, context = {}) {
  let timer = null;
  let disposed = false;
  let activeKey = "";
  let state = reactive({ retryAt: 0, kind: "cooldown", sending: false, everSent: false });
  const resolve = value => typeof value === "function" ? value() : value;
  const remaining = () => Math.max(0, Math.ceil((state.retryAt - Date.now()) / 1000));
  const sync = () => { targetRef.value = remaining(); };

  const clear = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const resume = () => {
    if (disposed) return;
    clear(); sync();
    timer = setInterval(sync, 1000);
  };

  const stopWatch = watch(() => {
    const scene = resolve(context.actionKey) || "default";
    const account = resolve(context.accountId) || (context.authRequired ? getToken() : "guest");
    return `${scopeHash(account)}:${scene}:${scopeHash(resolve(context.mobile) || "bound")}`;
  }, key => {
    activeKey = key;
    if (!waitStates.has(key)) {
      waitStates.set(key, reactive({ retryAt: 0,
        kind: "cooldown", sending: false, everSent: false }));
    }
    state = waitStates.get(key);
    resume();
  }, { immediate: true, flush: "sync" });

  window.addEventListener("focus", sync);
  document.addEventListener("visibilitychange", sync);
  const dispose = () => {
    disposed = true;
    clear(); stopWatch();
    window.removeEventListener("focus", sync);
    document.removeEventListener("visibilitychange", sync);
  };
  if (getCurrentScope()) onScopeDispose(dispose);

  return {
    start(seconds = COUNTDOWN_SECONDS, kind = "cooldown", lease = { state, key: activeKey }) {
      const duration = Number.isFinite(Number(seconds)) ? Math.max(0, Math.ceil(Number(seconds))) : COUNTDOWN_SECONDS;
      lease.state.retryAt = Math.max(lease.state.retryAt, Date.now() + duration * 1000);
      lease.state.kind = kind;
      lease.state.everSent ||= kind === "cooldown";
      if (lease.key === activeKey) resume();
    },
    acquire() {
      if (disposed || state.sending || remaining() > 0) return null;
      state.sending = true;
      return { state, key: activeKey };
    },
    release(lease) { if (lease) lease.state.sending = false; },
    get disabled() { return state.sending || targetRef.value > 0; },
    get label() {
      if (state.sending) return "发送中…";
      if (targetRef.value > 60) return "暂时无法获取";
      if (targetRef.value > 0) return `${targetRef.value}秒后重试`;
      return state.everSent || state.retryAt ? "重新获取" : "获取验证码";
    },
    get hint() {
      const seconds = targetRef.value;
      if (!seconds) return "";
      if (seconds > 60) {
        const duration = seconds >= 3600 ? `${Math.ceil(seconds / 3600)}小时` : `${Math.ceil(seconds / 60)}分钟`;
        return `${state.kind === "limited" ? "操作频繁，" : "请等待，"}约${duration}后可重试`;
      }
      return state.kind === "limited" ? "操作频繁，请稍后重试" : "验证码已发送";
    },
    clear,
    dispose,
  };
}

export const smsCaptchaGuardConfig = {
  WINDOW_MS,
  LIMIT_COUNT,
  COUNTDOWN_SECONDS,
};
